import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Modal, Progress, Tooltip, Typography, message } from 'antd';
import DownloadOutlined from '@ant-design/icons/DownloadOutlined';
import { EXPORT_SOURCES } from '../../lib/tableExportSources';
import type { ExportProgress } from '../../lib/tableExport';

const groups = Array.from(new Set(EXPORT_SOURCES.map(source => source.group)));
const label = 'DB ZIP 다운로드';

export default function TableExportButton({ collapsed = false }: { collapsed?: boolean }) {
  const [messageApi, contextHolder] = message.useMessage();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState<ExportProgress | null>(null);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);

  const start = async () => {
    if (controller.current) return;
    const current = new AbortController();
    controller.current = current;
    setBusy(true); setError(''); setProgress(null);
    try {
      const { exportVisibleTables } = await import('../../services/tableExport');
      current.signal.throwIfAborted();
      const result = await exportVisibleTables(current.signal, next => {
        if (mounted.current && !current.signal.aborted) setProgress(next);
      });
      current.signal.throwIfAborted();
      const url = URL.createObjectURL(new Blob([new Uint8Array(result.bytes)], { type: 'application/zip' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `raid-manager-tables-${new Date().toISOString().replace(/[:.]/g, '-')}.zip`;
      document.body.appendChild(anchor);
      try { anchor.click(); } finally { anchor.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 60_000); }
      setOpen(false);
      messageApi.success(`${result.tableCount}개 데이터, ${result.rowCount.toLocaleString()}행의 ZIP 다운로드를 시작했습니다.`);
    } catch (cause) {
      if (mounted.current && !current.signal.aborted) setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      if (controller.current === current) {
        controller.current = null;
        if (mounted.current) setBusy(false);
      }
    }
  };
  const close = () => { controller.current?.abort(); setOpen(false); };

  return <>
    {contextHolder}
    <Tooltip title={label}>
      <Button block icon={<DownloadOutlined />} aria-label={label} onClick={() => { setError(''); setProgress(null); setOpen(true); }} disabled={busy}>
        {!collapsed && label}
      </Button>
    </Tooltip>
    <Modal title="전체 데이터 ZIP 다운로드" open={open} onCancel={close} maskClosable={!busy} width={640}
      footer={<>
        <Button onClick={close}>{busy ? '취소' : '닫기'}</Button>
        <Button type="primary" aria-label={error ? '다시 시도' : 'JSON + CSV ZIP 다운로드'} icon={<DownloadOutlined />} onClick={start} loading={busy}>{error ? '다시 시도' : 'JSON + CSV ZIP 다운로드'}</Button>
      </>}>
      <Typography.Paragraph>
        모든 레이드의 보스·쫄몹, 룸, 스킬·효과, 트리거, 드롭템과 기준정보 {EXPORT_SOURCES.length}개를 한 번에 받습니다. 현재 선택한 보스나 검색 필터와 관계없이 조회 가능한 전체 데이터를 포함합니다.
      </Typography.Paragraph>
      <Typography.Paragraph type="secondary">
        ZIP 안에 테이블별 JSON·CSV와 파일별 행 수 목록(manifest.json)이 들어갑니다. 이미지는 저장된 주소를 포함하며 원본 파일은 포함하지 않습니다. 데이터 복원용 DB 백업은 아닙니다.
      </Typography.Paragraph>
      <details style={{ marginBottom: 16 }}>
        <summary style={{ cursor: 'pointer' }}>포함 데이터 목록 ({EXPORT_SOURCES.length}개)</summary>
        <div style={{ maxHeight: 240, overflow: 'auto', marginTop: 8 }}>
          {groups.map(group => <div key={group} style={{ marginBottom: 12 }}>
            <Typography.Text strong>{group}</Typography.Text>
            <ul>{EXPORT_SOURCES.filter(source => source.group === group).map(source => <li key={`${source.schema}.${source.table}`}>
              {source.label} <Typography.Text type="secondary" style={{ overflowWrap: 'anywhere' }}>({source.schema}.{source.table})</Typography.Text>
            </li>)}</ul>
          </div>)}
        </div>
      </details>
      {error && <Alert type="error" showIcon message="다운로드 실패" description={`${error} 누락된 파일로 ZIP을 만들지 않았습니다.`} />}
      {busy && <div role="status" aria-live="polite">
        <Progress percent={progress ? Math.min(99, Math.round(progress.completed / progress.total * 100)) : 0} status="active" />
        <Typography.Text style={{ overflowWrap: 'anywhere' }}>{!progress ? '로그인 확인 중…' : progress.table ? `${progress.completed} / ${progress.total} · ${progress.table} · ${progress.rows.toLocaleString()}행` : 'ZIP 파일을 만드는 중…'}</Typography.Text>
      </div>}
    </Modal>
  </>;
}
